import { useCallback } from 'react';
import { API_URL } from '../Config';

export function useTimeline(projectId, timelineItems, setTimelineItems) {

  const updateTimelineOrder = useCallback(async (newTimeline) => {
    const item_ids = newTimeline.map(item => item.id);
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/timeline-items/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ item_ids }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error updating timeline order:", error);
    }
  }, [projectId]);

  const handleUpdateText = useCallback(async (id, newText) => {
    try {
      const response = await fetch(`${API_URL}/timeline-items/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rawText: newText }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const updatedItem = await response.json();
      setTimelineItems((prevItems) =>
        prevItems.map((item) => (item.id === id ? updatedItem : item))
      );
    } catch (error) {
      console.error("Error updating timeline item text:", error);
    }
  }, [setTimelineItems]);

  const handleUpdateClipTimes = useCallback(async (id, newStartTime, newEndTime) => {
    console.log('handleUpdateClipTimes called:', { id, newStartTime, newEndTime });
    try {
      const response = await fetch(`${API_URL}/timeline-items/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ startTime: newStartTime, endTime: newEndTime }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const updatedItem = await response.json();
      setTimelineItems((prevItems) =>
        prevItems.map((item) => (item.id === id ? updatedItem : item))
      );
    } catch (error) {
      console.error("Error updating clip times:", error);
    }
  }, [setTimelineItems]);

  const handleUpdateSceneDescription = useCallback(async (id, newDescription) => {
    try {
      const response = await fetch(`${API_URL}/timeline-items/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ scene_description: newDescription }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const updatedItem = await response.json();
      setTimelineItems((prevItems) =>
        prevItems.map((item) => (item.id === id ? updatedItem : item))
      );
    } catch (error) {
      console.error("Error updating scene description:", error);
    }
  }, [setTimelineItems]);

  const handleUpdateSceneNarration = useCallback(async (id, newNarration, selectedLanguage) => {
    try {
      let updateBody = {};
      if (selectedLanguage === 'original') {
        updateBody = { scene_narration: newNarration };
      } else {
        updateBody = { translated_text: newNarration, language_code: selectedLanguage };
      }

      const response = await fetch(`${API_URL}/timeline-items/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateBody),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const updatedItem = await response.json();
      setTimelineItems((prevItems) =>
        prevItems.map((item) => (item.id === id ? updatedItem : item))
      );
    } catch (error) {
      console.error("Error updating scene narration:", error);
    }
  }, [setTimelineItems]);

  const handleRegenerateAudio = useCallback(async (itemId, setIsLoading, setMessage) => {
    setIsLoading(true);
    setMessage({ text: '', type: '' }); // Clear previous messages
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/generate-audio?timeline_item_id=${itemId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      console.log("Backend response for generate-audio:", JSON.stringify(result, null, 2)); // Log the full response as stringified JSON
      if (result.project && Array.isArray(result.project.timeline_items)) {
        const updatedItem = result.project.timeline_items.find(item => item.id === itemId);
        if (updatedItem) {
          setTimelineItems((prevItems) =>
            prevItems.map((item) => (item.id === itemId ? updatedItem : item))
          );
        } else {
          console.warn(`Updated item with ID ${itemId} not found in the backend response's timeline_items.`);
        }
      } else {
        console.error("Backend response did not contain expected 'project.timeline_items' array:", result);
        throw new Error("Invalid backend response structure.");
      }
      setMessage({ text: `Audio regenerated for item ${itemId} successfully!`, type: 'success' });
      console.log(`Audio regenerated for item ${itemId}:`, result);
    } catch (error) {
      setMessage({ text: `Failed to regenerate audio for item ${itemId}: ${error.message}`, type: 'error' });
      console.error(`Error regenerating audio for item ${itemId}:`, error);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, setTimelineItems]);

  return { updateTimelineOrder, handleUpdateText, handleUpdateClipTimes, handleUpdateSceneDescription, handleUpdateSceneNarration, handleRegenerateAudio };
}